<template>
  <div class="main-content">
    <!-- Header -->
    <div class="row mb-3">
      <div class="col-md-12">
        <div class="card">
          <div class="card-body d-flex align-items-center justify-content-between py-2">
            <div>
              <h4 class="mb-0">
                <i class="fas fa-tachometer-alt text-primary mr-2"></i>
                Resumo de Releases Abertas
              </h4>
            </div>
            <div class="d-flex align-items-center">
              <v-select
                v-model="filterProject"
                :options="projects"
                :reduce="p => p.id"
                label="name"
                placeholder="Filtrar por sistema"
                style="min-width: 220px"
                class="mr-2"
                @input="fetchReleases"
              />
              <v-select
                v-model="filterStatus"
                :options="statusFilterOptions"
                :reduce="s => s.value"
                label="label"
                placeholder="Filtrar por status"
                style="min-width: 200px"
                class="mr-2"
                @input="fetchReleases"
              />
              <button class="btn btn-outline-secondary btn-sm mr-2" @click="clearFilters">
                <i class="fas fa-times mr-1"></i> Limpar
              </button>
              <button class="btn btn-primary btn-sm" @click="openNewReleaseModal">
                <i class="fas fa-plus mr-1"></i> Nova Release
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- KPI Cards -->
    <div class="row mb-3">
      <div class="col-md-3">
        <div class="card border-left-primary">
          <div class="card-body py-2 d-flex align-items-center justify-content-between">
            <div>
              <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">Em Andamento</div>
              <div class="h5 mb-0 font-weight-bold">{{ counts.em_andamento }}</div>
            </div>
            <i class="fas fa-code-branch fa-2x text-primary opacity-50"></i>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card border-left-danger">
          <div class="card-body py-2 d-flex align-items-center justify-content-between">
            <div>
              <div class="text-xs font-weight-bold text-danger text-uppercase mb-1">Com Atraso</div>
              <div class="h5 mb-0 font-weight-bold">{{ counts.atrasadas }}</div>
            </div>
            <i class="fas fa-exclamation-triangle fa-2x text-danger opacity-50"></i>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card border-left-warning">
          <div class="card-body py-2 d-flex align-items-center justify-content-between">
            <div>
              <div class="text-xs font-weight-bold text-warning text-uppercase mb-1">Aguardando QA</div>
              <div class="h5 mb-0 font-weight-bold">{{ counts.aguardando_qa }}</div>
            </div>
            <i class="fas fa-user-check fa-2x text-warning opacity-50"></i>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card border-left-success">
          <div class="card-body py-2 d-flex align-items-center justify-content-between">
            <div>
              <div class="text-xs font-weight-bold text-success text-uppercase mb-1">Aguardando Deploy</div>
              <div class="h5 mb-0 font-weight-bold">{{ counts.aguardando_deploy }}</div>
            </div>
            <i class="fas fa-rocket fa-2x text-success opacity-50"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-body p-0">
            <div v-if="loading" class="text-center py-5">
              <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
            </div>
            <div v-else-if="filteredReleases.length === 0" class="text-center py-5 text-muted">
              <i class="fas fa-check-circle fa-3x mb-2 text-success"></i>
              <p class="mb-0">Nenhuma release aberta encontrada.</p>
            </div>
            <table v-else class="table table-hover table-bordered mb-0">
              <thead class="thead-light">
                <tr>
                  <th style="width: 100px">Versão</th>
                  <th>Nome</th>
                  <th>Sistema</th>
                  <th>Status</th>
                  <th>Fim Desenvolvimento</th>
                  <th>Início Testes</th>
                  <th>Fim Testes</th>
                  <th>Deploy Previsto</th>
                  <th class="text-center" style="width: 100px">Chamados</th>
                  <th class="text-center" style="width: 60px">Ver</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="release in filteredReleases"
                  :key="release.id"
                  :class="rowClass(release)"
                >
                  <td><strong>{{ release.version_string }}</strong></td>
                  <td>
                    {{ release.name }}
                  </td>
                  <td>{{ release.project ? release.project.name : '—' }}</td>
                  <td>
                    <span :class="statusBadge(release.status)">
                      {{ statusLabel(release.status) }}
                    </span>
                    <span v-if="isAlertStatus(release.status)" class="ml-1" :title="statusLabel(release.status)">
                      <i :class="alertIcon(release.status)"></i>
                    </span>
                  </td>
                  <td>{{ release.data_fim_desenvolvimento_f || '—' }}</td>
                  <td :class="{ 'text-danger': isDatePast(release.data_prevista_inicio_testes) }">
                    {{ release.data_prevista_inicio_testes_f || '—' }}
                  </td>
                  <td :class="{ 'text-danger font-weight-bold': isDatePast(release.data_fim_testes) && isTestingStatus(release.status) }">
                    {{ release.data_fim_testes_f || '—' }}
                  </td>
                  <td :class="{ 'text-danger font-weight-bold': isDatePast(release.programacao_inicial_deploy) && release.status === 'aguardando_deploy' }">
                    {{ release.programacao_inicial_deploy_f || '—' }}
                  </td>
                  <td class="text-center">
                    <span class="badge badge-info">{{ release.tickets_count || 0 }}</span>
                  </td>
                  <td class="text-center">
                    <router-link
                      :to="`/projects/${release.project_id}/releases/view/${release.id}`"
                      class="btn btn-sm btn-crud view"
                      title="Ver release"
                    >
                      <i class="fas fa-eye"></i>
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      v-model="newReleaseModal"
      id="modalNewRelease"
      title="Nova Release"
      centered
      @ok="goToNewRelease"
      :ok-disabled="!newReleaseProjectId"
      ok-title="Continuar"
      cancel-title="Cancelar"
    >
      <label>Sistema</label>
      <v-select
        v-model="newReleaseProjectId"
        :options="projects"
        :reduce="p => p.id"
        label="name"
        placeholder="Selecione o sistema..."
      />
    </b-modal>
  </div>
</template>

<script>
export default {
  data() {
    return {
      releases: [],
      projects: [],
      loading: true,
      filterProject: null,
      filterStatus: null,
      newReleaseModal: false,
      newReleaseProjectId: null,
      statusFilterOptions: [
        { value: 'planejada', label: 'Planejada' },
        { value: 'em_desenvolvimento', label: 'Em Desenvolvimento' },
        { value: 'desenvolvimento_atrasado', label: 'Desenvolvimento Atrasado' },
        { value: 'aguardando_testes', label: 'Aguardando Testes' },
        { value: 'em_testes', label: 'Em Testes' },
        { value: 'testes_atrasados', label: 'Testes Atrasados' },
        { value: 'aguardando_aprovacao_qa', label: 'Aguardando Aprovação QA' },
        { value: 'aguardando_deploy', label: 'Aguardando Deploy' },
        { value: 'deploy_atrasado', label: 'Deploy Atrasado' },
      ],
    };
  },

  computed: {
    filteredReleases() {
      let list = this.releases;
      if (this.filterStatus) {
        list = list.filter(r => r.status === this.filterStatus);
      }
      return list;
    },

    counts() {
      const atrasados = ['planejamento_atrasado', 'desenvolvimento_atrasado', 'testes_atrasados', 'deploy_atrasado'];
      return {
        em_andamento: this.releases.length,
        atrasadas: this.releases.filter(r => atrasados.includes(r.status)).length,
        aguardando_qa: this.releases.filter(r => r.status === 'aguardando_aprovacao_qa').length,
        aguardando_deploy: this.releases.filter(r => r.status === 'aguardando_deploy').length,
      };
    },
  },

  mounted() {
    this.fetchProjects();
    this.fetchReleases();
  },

  methods: {
    openNewReleaseModal() {
      this.newReleaseProjectId = null;
      this.newReleaseModal = true;
    },

    goToNewRelease() {
      if (!this.newReleaseProjectId) return;
      this.$router.push(`/projects/${this.newReleaseProjectId}/releases/add`);
    },

    fetchProjects() {
      axios
        .get(this.$store.state.api + 'projects?paginated=false')
        .then(res => {
          this.projects = res.data.data || res.data;
        })
        .catch(() => {});
    },

    fetchReleases() {
      this.loading = true;
      let url = this.$store.state.api + 'releases?paginated=false&withCount[]=tickets&with[]=project';
      if (this.filterProject) url += `&project_id=${this.filterProject}`;
      // Exclude closed/finalized
      url += '&exclude_status=fechada,finalizada';

      axios
        .get(url)
        .then(res => {
          let data = res.data.data || res.data;
          // Filter out closed/finalized client-side as fallback
          this.releases = data.filter(r => !['fechada', 'finalizada'].includes(r.status));
        })
        .catch(() => {
          this.$message('Erro', 'Não foi possível carregar as releases.', 'error');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    clearFilters() {
      this.filterProject = null;
      this.filterStatus = null;
      this.fetchReleases();
    },

    statusLabel(status) {
      const map = {
        planejada: 'Planejada',
        em_desenvolvimento: 'Em Desenvolvimento',
        em_testes: 'Em Testes',
        fechada: 'Fechada',
        finalizada: 'Finalizada',
        desenvolvimento_atrasado: 'Desenvolvimento Atrasado',
        aguardando_testes: 'Aguardando Testes',
        testes_atrasados: 'Testes Atrasados',
        aguardando_aprovacao_qa: 'Aguardando Aprovação QA',
        aguardando_deploy: 'Aguardando Deploy',
        deploy_atrasado: 'Deploy Atrasado',
      };
      return map[status] || status;
    },

    statusBadge(status) {
      const map = {
        planejada: 'badge badge-secondary',
        em_desenvolvimento: 'badge badge-primary',
        em_testes: 'badge badge-warning',
        fechada: 'badge badge-success',
        finalizada: 'badge badge-info',
        desenvolvimento_atrasado: 'badge badge-danger',
        aguardando_testes: 'badge badge-info',
        testes_atrasados: 'badge badge-danger',
        aguardando_aprovacao_qa: 'badge badge-warning',
        aguardando_deploy: 'badge badge-primary',
        deploy_atrasado: 'badge badge-danger',
      };
      return map[status] || 'badge badge-secondary';
    },

    rowClass(release) {
      const danger = ['planejamento_atrasado', 'desenvolvimento_atrasado', 'testes_atrasados', 'deploy_atrasado'];
      if (danger.includes(release.status)) return 'table-danger';
      if (release.status === 'aguardando_aprovacao_qa') return 'table-warning';
      return '';
    },

    isAlertStatus(status) {
      return ['planejamento_atrasado', 'desenvolvimento_atrasado', 'testes_atrasados', 'deploy_atrasado', 'aguardando_aprovacao_qa'].includes(status);
    },

    alertIcon(status) {
      if (['desenvolvimento_atrasado', 'testes_atrasados', 'deploy_atrasado'].includes(status)) {
        return 'fas fa-exclamation-triangle text-danger';
      }
      if (status === 'aguardando_aprovacao_qa') {
        return 'fas fa-user-clock text-warning';
      }
      return '';
    },

    isTestingStatus(status) {
      return ['em_testes', 'aguardando_testes', 'testes_atrasados'].includes(status);
    },

    isDatePast(dateStr) {
      if (!dateStr) return false;
      // dateStr is in format d/m/Y from the API appended attribute
      const parts = dateStr.split('/');
      if (parts.length !== 3) return false;
      const date = new Date(parts[2], parts[1] - 1, parts[0]);
      return date < new Date();
    },
  },
};
</script>

<style scoped>
.border-left-primary { border-left: 4px solid #4e73df !important; }
.border-left-danger  { border-left: 4px solid #e74a3b !important; }
.border-left-warning { border-left: 4px solid #f6c23e !important; }
.border-left-success { border-left: 4px solid #1cc88a !important; }
.opacity-50 { opacity: 0.4; }
.text-xs { font-size: 0.75rem; }
</style>
